// The open loop API as the console's pages call it, in the shapes those pages were written against (the legacy
// AdminApiClient's records): statuses, roles, processors and features in PascalCase, money as numbers, pages with a
// totalCount. The API decides who may do what; the console only asks. Reads that fail are "nothing", as
// AdminApiClient returned them; writes give the API's message.
import {
  amount,
  api,
  API_URL,
  BffError,
  download,
  newIdempotencyKey,
  pascal,
  providerKey,
  providerName,
  query,
  seg,
  storeProvider,
} from '@qor/web-shared';

export interface Page<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}
export const totalPages = (p: Pick<Page<unknown>, 'totalCount' | 'pageSize'>) =>
  p.pageSize <= 0 ? 0 : Math.ceil(p.totalCount / p.pageSize);
const emptyPage = <T>(page: number, pageSize: number): Page<T> => ({
  items: [],
  totalCount: 0,
  page,
  pageSize,
});

export interface CardholderSummary {
  id: string;
  name: string;
  email: string;
  status: string;
  programName: string;
  provider: string;
}
export interface PointsCardholder {
  id: string;
  name: string;
  email: string;
  programName: string;
  provider: string;
  pointsBalance: number;
  cashValue: number | null;
  currency: string;
}
export interface ProgramSummary {
  id: string;
  /** The Entity ID (internal): four digits for partners added since 2026-10-23. */
  code: string;
  displayName: string;
  provider: string;
  isActive: boolean;
  parentProgramId: string | null;
  parentProgramName: string | null;
  /** Instant issue: staff issue each card with its amount (no invitation, no identity check). */
  instantIssue: boolean;
  /** Whether the program's processor can issue cards directly (QOR may switch it on). */
  instantIssueAvailable: boolean;
  /** Kiosks on (a location shows its partner's): the Kiosks tab shows only then. */
  kiosksEnabled: boolean;
}

/** Where a card issued by staff is sent. */
export interface CardAddress {
  street: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
}
export interface CardIssue {
  programId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  /** null: issue the card without loading it. */
  amount: number | null;
  address: CardAddress;
}
/** What issuing a card came to: the card, and what became of its first load. */
export interface CardIssued {
  success: boolean;
  /** The card is issued but its load did not go through, or waits: the words for the page. */
  message: string | null;
  cardholderId: string | null;
  last4: string | null;
}
export interface ProgramFeatureState {
  feature: string;
  isEnabled: boolean;
}
export interface BrandProfile {
  programId: string;
  domainHost: string | null;
  displayName: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
}
export interface UserSummary {
  id: string;
  email: string;
  displayName: string;
  role: string;
  programName: string;
  hasPassword: boolean;
  status: string;
  programId: string;
  provider: string;
  /** An authenticator app is on. */
  twoFactor?: boolean;
}
export interface AuditEntry {
  id: string;
  occurredAt: string;
  actorEmail: string;
  actorDisplayName: string;
  action: string;
  targetType: string;
  targetId: string | null;
  targetLabel: string;
  details: string | null;
  actorIpAddress: string | null;
}
export interface AuditPage extends Page<AuditEntry> {
  availableActions: string[];
}
export interface TransactionSummary {
  id: string;
  cardholderName: string;
  cardholderEmail: string;
  programName: string;
  provider: string;
  transactionType: string;
  description: string;
  amount: number;
  currency: string;
  status: string;
  postedAt: string | null;
}
export interface CardholderDetail {
  id: string;
  name: string;
  email: string;
  phoneNumber: string | null;
  status: string;
  programId: string;
  programName: string;
  provider: string;
  providerUserReference: string | null;
  createdAt: string;
  /** The identity record kept with the last application: its status and the SSN's last four only. */
  kyc: CardholderKyc | null;
  /** A QOR sign-in: none for a cardholder whose card staff issued, or a partner's (no password to set). */
  hasSignIn: boolean;
}
export interface CardholderKyc {
  /** Approved | QuestionsRequired | DocumentsRequired | UnderReview | Denied */
  status: string;
  provider: string;
  ssnLast4: string | null;
  submittedAt: string;
  decidedAt: string | null;
}
/** The kept identity numbers, opened for a system admin (every look is audited). */
export interface RevealedKyc {
  ssn: string | null;
  dateOfBirth: string;
  idNumber: string | null;
}
export interface PointsRate {
  programId: string | null;
  programName: string | null;
  pointsPerUnit: number;
  currency: string;
  expiresOn: string | null;
}
export interface PointsRates {
  default: PointsRate;
  programOverrides: PointsRate[];
  availablePrograms: { id: string; displayName: string }[];
}
export interface PointsLedgerEntry {
  id: string;
  type: string;
  pointsDelta: number;
  note: string | null;
  cashAmount: number | null;
  currency: string | null;
  createdAt: string;
  /** Where the points came from, in words (earn: the purchase, the rule and the sum; why fewer). */
  reason: string | null;
}
export interface PointsBalance {
  cardholderId: string;
  balance: number;
  pointsPerUnit: number;
  currency: string;
  history: PointsLedgerEntry[];
}
export interface RedeemResult {
  success: boolean;
  message: string;
  newBalance: number;
  cashAmount: number;
  currency: string;
}
export interface ProbeResult {
  providerName?: string;
  reachable: boolean;
  category: string;
  httpStatusCode: number | null;
  elapsedMs: number;
  target: string;
  message: string;
  checkedAtUtc: string;
}
export interface MdpStatus {
  baseUrlConfigured: boolean;
  baseUrl: string;
  instIdConfigured: boolean;
  tokenConfigured: boolean;
  tokenExpiresAtUtc: string | null;
}
/** One real HTTP exchange with the API, as the kiosk simulator shows it. */
export interface KioskExchange {
  method: string;
  path: string;
  requestJson: string;
  statusCode: number;
  responseJson: string;
  success: boolean;
}

/** A read that fails is the fallback (AdminApiClient: empty page, empty list, null). */
async function orElse<T>(call: Promise<T>, fallback: T): Promise<T> {
  try {
    return (await call) ?? fallback;
  } catch (err) {
    if (err instanceof BffError) return fallback;
    throw err;
  }
}

export interface WriteResult {
  success: boolean;
  error: string | null;
}

/** A write: success, or the API's message. */
async function write(call: Promise<unknown>): Promise<WriteResult> {
  try {
    await call;
    return { success: true, error: null };
  } catch (err) {
    if (err instanceof BffError) return { success: false, error: err.message };
    throw err;
  }
}

export interface CardholderFilter {
  search?: string | null;
  provider?: string | null;
  status?: string | null;
  programId?: string | null;
  page: number;
  pageSize: number;
}

/** The API's pages ({ items, total }) as the console's ({ items, totalCount }). */
interface ApiPage<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
const asPage = <A, B>(p: ApiPage<A>, map: (a: A) => B): Page<B> => ({
  items: p.items.map(map),
  totalCount: p.total,
  page: p.page,
  pageSize: p.pageSize,
});

/** "PendingOnboarding" -> "pending_onboarding" (filters as the API spells them). */
const snake = (value: string | null | undefined) =>
  value ? value.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase() : null;

const API_ORIGIN = API_URL.replace(/\/api\/v1$/, '');

interface ApiProgram {
  id: string;
  code: string;
  displayName: string;
  provider: string | null;
  isActive: boolean;
  isInternal: boolean;
  instantIssue?: boolean;
  instantIssueAvailable?: boolean;
  kiosksEnabled?: boolean;
  parentId: string | null;
  parentName: string | null;
}
interface ApiCardholder {
  id: string;
  name: string;
  email: string;
  status: string;
  program: { id: string; displayName: string };
  provider: string | null;
  createdAt: string;
}
interface ApiCardholderDetail extends ApiCardholder {
  phone: string | null;
  cards: { id: string; status: string }[];
  kyc: {
    status: string;
    provider: string;
    ssnLast4: string | null;
    submittedAt: string;
    decidedAt: string | null;
  } | null;
  account: {
    status: string;
    emailVerified: boolean;
    lastLoginAt: string | null;
    twoFactor: boolean;
  } | null;
}
interface ApiStaff {
  id: string;
  email: string;
  displayName: string;
  role: string;
  status: string;
  program: { id: string; displayName: string; isInternal: boolean };
  twoFactor: string;
  lastLoginAt: string | null;
}
interface ApiBrand {
  programId: string;
  domainHost: string | null;
  displayName: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
}

const toProgram = (p: ApiProgram): ProgramSummary => ({
  id: p.id,
  code: p.code,
  displayName: p.displayName,
  provider: providerName(p.provider),
  isActive: p.isActive,
  parentProgramId: p.parentId,
  parentProgramName: p.parentName,
  instantIssue: p.instantIssue ?? false,
  instantIssueAvailable: p.instantIssueAvailable ?? false,
  kiosksEnabled: p.kiosksEnabled ?? false,
});
const toBrand = (b: ApiBrand): BrandProfile => ({
  ...b,
  logoUrl: b.logoUrl ? `${API_ORIGIN}${b.logoUrl}` : null,
});

/** The card in use: the newest that is not closed (a replaced card stays on file). */
const currentCard = (cards: { id: string; status: string }[]) =>
  [...cards].reverse().find((c) => c.status !== 'closed') ?? cards.at(-1) ?? null;

/** One program's features (opt-out: everything on unless switched off), the console's way. */
async function featuresOf(programId: string): Promise<ProgramFeatureState[]> {
  const flags = await api<Record<string, boolean>>('GET', `/admin/programs/${seg(programId)}/features`);
  return Object.entries(flags).map(([feature, isEnabled]) => ({
    feature: pascal(feature) ?? feature,
    isEnabled,
  }));
}

export const adminApi = {
  cardholders: (f: CardholderFilter) =>
    orElse(
      api<ApiPage<ApiCardholder>>(
        'GET',
        `/admin/cardholders${query({ search: f.search?.trim(), provider: providerKey(f.provider), status: snake(f.status), programId: f.programId, page: f.page, pageSize: f.pageSize })}`,
      ).then((p) =>
        asPage(p, (c) => ({
          id: c.id,
          name: c.name,
          email: c.email,
          status: pascal(c.status) ?? c.status,
          programName: c.program.displayName,
          provider: providerName(c.provider),
        })),
      ),
      emptyPage<CardholderSummary>(f.page, f.pageSize),
    ),
  pointsCardholders: (f: Omit<CardholderFilter, 'status'> & { hasPoints?: boolean | null }) =>
    orElse(
      api<
        ApiPage<{
          cardholderId: string;
          name: string;
          email: string;
          programName: string;
          provider: string | null;
          balance: number;
          cashValue: string;
          currency: string;
        }>
      >(
        'GET',
        `/admin/points/cardholders${query({ search: f.search?.trim(), provider: providerKey(f.provider), programId: f.programId, hasPoints: f.hasPoints ? 'true' : null, page: f.page, pageSize: f.pageSize })}`,
      ).then((p) =>
        asPage(p, (h) => ({
          id: h.cardholderId,
          name: h.name,
          email: h.email,
          programName: h.programName,
          provider: providerName(h.provider),
          pointsBalance: h.balance,
          cashValue: amount(h.cashValue),
          currency: h.currency,
        })),
      ),
      emptyPage<PointsCardholder>(f.page, f.pageSize),
    ),
  cardholder: (id: string) =>
    orElse(
      api<ApiCardholderDetail>('GET', `/admin/cardholders/${seg(id)}`).then((c): CardholderDetail => ({
        id: c.id,
        name: c.name,
        email: c.email,
        phoneNumber: c.phone,
        status: pascal(c.status) ?? c.status,
        programId: c.program.id,
        programName: c.program.displayName,
        provider: providerName(c.provider),
        // the console's card pages name a card "cardholderId:cardId" (shared/cardOps.ts)
        providerUserReference: currentCard(c.cards) ? `${c.id}:${currentCard(c.cards)!.id}` : null,
        createdAt: c.createdAt,
        kyc: c.kyc && {
          ...c.kyc,
          status: pascal(c.kyc.status) ?? c.kyc.status,
          provider: providerName(c.kyc.provider),
        },
        hasSignIn: c.account !== null,
      })),
      null,
    ),
  /** The kept SSN, date of birth and ID number in clear: system admins only, audited. */
  revealKyc: async (cardholderId: string): Promise<RevealedKyc | { error: string }> => {
    try {
      return await api<RevealedKyc>('POST', `/admin/cardholders/${seg(cardholderId)}/kyc/reveal`);
    } catch (err) {
      if (err instanceof BffError) return { error: err.message };
      throw err;
    }
  },
  /** Ask the processor to verify the cardholder again. */
  retryKyc: (cardholderId: string) => write(api('POST', `/admin/cardholders/${seg(cardholderId)}/kyc/retry`)),
  createCardholder: (body: { email: string; firstName: string; lastName: string | null; programId: string }) =>
    write(api('POST', '/admin/cardholders', { body })),
  /** The cardholder chooses their own password: staff send them a code by e-mail (never a password they could read). */
  /** A generated password, answered once (the API keeps only its hash). */
  setCardholderPassword: (cardholderId: string) =>
    outcome(api<{ password: string }>('POST', `/admin/cardholders/${seg(cardholderId)}/password`)),
  sendPasswordCode: async (cardholderId: string) =>
    (await write(api('POST', `/admin/cardholders/${seg(cardholderId)}/password-link`))).success,

  programs: () =>
    orElse(
      api<ApiProgram[]>('GET', '/admin/programs').then((list) => list.filter((p) => !p.isInternal).map(toProgram)),
      [],
    ),
  /** code: the Entity ID; empty = the API picks a random free one. */
  createProgram: async (body: {
    displayName: string;
    code: string;
    primaryProvider: string;
    parentProgramId: string | null;
  }): Promise<WriteResult & { id: string | null }> => {
    let id: string | null = null;
    const result = await write(
      api<ApiProgram>('POST', '/admin/programs', {
        body: {
          displayName: body.displayName,
          ...(body.code.trim() !== '' && { code: body.code.trim() }),
          primaryProvider: providerKey(body.primaryProvider),
          parentId: body.parentProgramId,
        },
      }).then((p) => (id = p.id)),
    );
    return { ...result, id };
  },
  /** QOR's system admins: staff issue the program's cards (or cardholders are invited again). */
  setInstantIssue: (id: string, enabled: boolean) =>
    write(api('PUT', `/admin/programs/${seg(id)}/instant-issue`, { body: { enabled } })),
  /**
   * Issue a card with its amount, under one Idempotency-Key per submit (the same submit retried never issues or
   * loads twice). A card issued whose load failed or waits for approval is still a card: the message says what to do.
   */
  issueInstantCard: async (body: CardIssue, idempotencyKey: string): Promise<CardIssued> => {
    try {
      const issued = await api<{
        cardholderId: string;
        card: { last4: string | null };
        load: { status: string; message: string | null } | null;
      }>('POST', '/admin/instant-cards', {
        body: { ...body, amount: body.amount === null ? null : body.amount.toFixed(2), phone: body.phone || null },
        idempotencyKey,
      });
      const { status, message } = issued.load ?? { status: 'none', message: null };
      return {
        success: true,
        cardholderId: issued.cardholderId,
        last4: issued.card.last4,
        message:
          status === 'succeeded' || status === 'none'
            ? null
            : status === 'pending_approval'
              ? 'The card is issued. Its amount is above the approval threshold: it waits for another admin under Loads.'
              : status === 'failed'
                ? `The card is issued, but the processor refused its load: ${message ?? ''} Add funds from the card page.`
                : 'The card is issued, but the processor did not answer its load. Do NOT load it again: check Loads first.',
      };
    } catch (err) {
      if (!(err instanceof BffError)) throw err;
      const details = (err.body as { details?: { cardholderId?: string } } | null)?.details;
      return { success: false, message: err.message, cardholderId: details?.cardholderId ?? null, last4: null };
    }
  },
  setProgramActive: (id: string, isActive: boolean) =>
    write(api('PATCH', `/admin/programs/${seg(id)}`, { body: { isActive } })),
  setProgramCode: (id: string, code: string) => write(api('PATCH', `/admin/programs/${seg(id)}`, { body: { code } })),
  setKiosks: (id: string, enabled: boolean) =>
    write(api('PUT', `/admin/programs/${seg(id)}/kiosks`, { body: { enabled } })),
  programFeatures: (id: string) => orElse(featuresOf(id), []),
  setProgramFeatures: (id: string, features: ProgramFeatureState[]) =>
    write(
      api('PUT', `/admin/programs/${seg(id)}/features`, {
        body: {
          features: Object.fromEntries(features.map((f) => [snake(f.feature), f.isEnabled])),
        },
      }),
    ),
  branding: (id: string) => orElse(api<ApiBrand>('GET', `/admin/programs/${seg(id)}/branding`).then(toBrand), null),
  setBranding: (
    id: string,
    body: {
      domainHost: string | null;
      displayName: string;
      logoUrl: string | null;
      primaryColor: string;
      secondaryColor: string;
    },
  ) =>
    // A logo is uploaded, never linked: an outside address is not shown to cardholders (it would see who opens it).
    body.logoUrl && !body.logoUrl.startsWith(API_ORIGIN)
      ? Promise.resolve({
          success: false,
          error: 'Logos are uploaded, not linked: choose the image file instead of pasting an address.',
        })
      : write(
          api('PUT', `/admin/programs/${seg(id)}/branding`, {
            body: {
              displayName: body.displayName,
              primaryColor: body.primaryColor,
              secondaryColor: body.secondaryColor,
              domainHost: body.domainHost?.trim() || null,
            },
          }),
        ),
  removeBranding: async (id: string) => (await write(api('DELETE', `/admin/programs/${seg(id)}/branding`))).success,
  /** The image itself is the request body; the API reads what it really is. */
  uploadLogo: async (
    id: string,
    file: File,
  ): Promise<{
    success: boolean;
    logoUrl: string | null;
    error: string | null;
  }> => {
    try {
      const b = await api<ApiBrand>('PUT', `/admin/programs/${seg(id)}/branding/logo`, { body: file });
      return { success: true, logoUrl: toBrand(b).logoUrl, error: null };
    } catch (err) {
      if (err instanceof BffError) return { success: false, logoUrl: null, error: err.message };
      throw err;
    }
  },
  /** The features a program has switched off. */
  disabledFeatures: async (programId: string) =>
    (await orElse(featuresOf(programId), [])).filter((f) => !f.isEnabled).map((f) => f.feature),
  /** A program's money limits (one load, per card per day, per program per day, before KYC, approval threshold). */
  limits: (id: string) => orElse(api<ProgramLimits>('GET', `/admin/programs/${seg(id)}/limits`), null),
  setLimits: (id: string, body: Omit<ProgramLimits, 'isDefault'>) =>
    write(api('PUT', `/admin/programs/${seg(id)}/limits`, { body })),

  users: () =>
    orElse(
      Promise.all([api<ApiStaff[]>('GET', '/admin/staff'), api<ApiProgram[]>('GET', '/admin/programs')]).then(
        ([staff, programs]) => {
          const programOf = new Map(programs.map((p) => [p.id, p]));
          return staff.map((s): UserSummary => ({
            id: s.id,
            email: s.email,
            displayName: s.displayName,
            role: pascal(s.role) ?? s.role,
            programName: s.program.displayName,
            hasPassword: s.status !== 'invited',
            status: pascal(s.status) ?? s.status,
            programId: s.program.id,
            provider: providerName(programOf.get(s.program.id)?.provider ?? null),
            twoFactor: s.twoFactor === 'totp',
          }));
        },
      ),
      [],
    ),
  createUser: (body: { email: string; displayName: string; programId: string; role: string }) =>
    write(
      api('POST', '/admin/staff', {
        body: { ...body, role: snake(body.role) },
      }),
    ),
  setUserRole: (id: string, role: string) =>
    write(api('PATCH', `/admin/staff/${seg(id)}`, { body: { role: snake(role) } })),
  setUserActive: (id: string, isActive: boolean) =>
    write(
      api('PATCH', `/admin/staff/${seg(id)}`, {
        body: { status: isActive ? 'active' : 'disabled' },
      }),
    ),
  /** A staff member's password is theirs: an invitation again (not signed in yet), else a reset code by e-mail. */
  setStaffPassword: (staffId: string) =>
    outcome(api<{ password: string }>('POST', `/admin/staff/${seg(staffId)}/password`)),
  sendStaffPasswordCode: async (user: Pick<UserSummary, 'id' | 'email' | 'hasPassword'>) =>
    (
      await write(
        user.hasPassword
          ? api('POST', '/auth/password/forgot', {
              body: { app: 'admin', email: user.email },
              noRefresh: true,
            })
          : api('POST', `/admin/staff/${seg(user.id)}/invite`),
      )
    ).success,
  /** Turn someone's authenticator off (a lost phone). */
  resetTwoFactor: async (kind: 'staff' | 'cardholder', id: string) =>
    (await write(api('POST', `/admin/${kind === 'staff' ? 'staff' : 'cardholders'}/${seg(id)}/two-factor/reset`)))
      .success,
  twoFactorOf: async (kind: 'staff' | 'cardholder', id: string) => {
    if (kind === 'staff')
      return {
        enabled: (await api<ApiStaff>('GET', `/admin/staff/${seg(id)}`)).twoFactor === 'totp',
      };
    return {
      enabled: (await api<ApiCardholderDetail>('GET', `/admin/cardholders/${seg(id)}`)).account?.twoFactor === true,
    };
  },

  auditLog: (f: {
    actor?: string | null;
    target?: string | null;
    action?: string | null;
    from?: string | null;
    to?: string | null;
    page: number;
    pageSize: number;
  }) =>
    orElse(
      api<
        ApiPage<{
          id: string;
          occurredAt: string;
          actorLabel: string;
          action: string;
          targetType: string;
          targetId: string | null;
          targetLabel: string | null;
          details: unknown;
          ip: string | null;
        }> & { availableActions: string[] }
      >('GET', `/admin/audit-log${query({ ...f, pageSize: Math.min(f.pageSize, 100) })}`).then((p) => ({
        ...asPage(p, (e) => ({
          id: e.id,
          occurredAt: e.occurredAt,
          actorEmail: e.actorLabel,
          actorDisplayName: e.actorLabel,
          action: e.action,
          targetType: e.targetType,
          targetId: e.targetId,
          targetLabel: e.targetLabel ?? e.targetId ?? '',
          details: e.details === null || e.details === undefined ? null : JSON.stringify(e.details),
          actorIpAddress: e.ip,
        })),
        availableActions: p.availableActions,
      })),
      {
        ...emptyPage<AuditEntry>(f.page, f.pageSize),
        availableActions: [],
      },
    ),
  transactions: (f: Omit<CardholderFilter, 'status'> & { type?: string | null }) =>
    orElse(
      api<
        ApiPage<{
          id: string;
          cardholderName: string;
          cardholderEmail: string;
          programName: string;
          provider: string;
          type: string;
          description: string;
          amount: string;
          currency: string;
          occurredAt: string;
        }>
      >(
        'GET',
        `/admin/transactions${query({ search: f.search?.trim(), provider: providerKey(f.provider), type: snake(f.type), programId: f.programId, page: f.page, pageSize: Math.min(f.pageSize, 100) })}`,
      ).then((p) =>
        asPage(p, (t) => ({
          id: t.id,
          cardholderName: t.cardholderName,
          cardholderEmail: t.cardholderEmail,
          programName: t.programName,
          provider: providerName(t.provider),
          transactionType: pascal(t.type) ?? 'Unknown',
          description: t.description,
          amount: Number(t.amount),
          currency: t.currency,
          status: 'Posted',
          postedAt: t.occurredAt,
        })),
      ),
      emptyPage<TransactionSummary>(f.page, f.pageSize),
    ),

  pointsRates: () =>
    orElse(
      Promise.all([
        api<{
          default: {
            pointsPerUnit: number;
            currency: string;
            expiresOn: string | null;
          } | null;
          programs: {
            programId: string;
            programName: string | null;
            pointsPerUnit: number;
            currency: string;
            expiresOn: string | null;
          }[];
        }>('GET', '/admin/points/rates'),
        api<ApiProgram[]>('GET', '/admin/programs'),
      ]).then(([rates, programs]): PointsRates => ({
        default: {
          programId: null,
          programName: null,
          pointsPerUnit: rates.default?.pointsPerUnit ?? 0,
          currency: rates.default?.currency ?? 'USD',
          expiresOn: rates.default?.expiresOn ?? null,
        },
        programOverrides: rates.programs.map((r) => ({ ...r })),
        availablePrograms: programs
          .filter((p) => !p.isInternal && p.parentId === null)
          .map((p) => ({ id: p.id, displayName: p.displayName })),
      })),
      null,
    ),
  setDefaultRate: (pointsPerUnit: number, currency: string) =>
    write(
      api('PUT', '/admin/points/rates/default', {
        body: { pointsPerUnit, currency },
      }),
    ),
  setProgramRate: (programId: string, pointsPerUnit: number, currency: string, expiresOn: string | null) =>
    write(
      api('PUT', `/admin/points/rates/programs/${seg(programId)}`, {
        body: { pointsPerUnit, currency, expiresOn },
      }),
    ),
  removeProgramRate: async (programId: string) =>
    (await write(api('DELETE', `/admin/points/rates/programs/${seg(programId)}`))).success,
  pointsBalance: (cardholderId: string) =>
    orElse(
      api<{
        balance: number;
        rate: { pointsPerUnit: number; currency: string };
        entries: {
          id: string;
          type: string;
          points: number;
          note: string | null;
          externalId: string | null;
          details: Record<string, unknown> | null;
          cash: string | null;
          currency: string | null;
          createdAt: string;
        }[];
      }>('GET', `/admin/cardholders/${seg(cardholderId)}/points`).then((b): PointsBalance => ({
        cardholderId,
        balance: b.balance,
        pointsPerUnit: b.rate.pointsPerUnit,
        currency: b.rate.currency,
        history: b.entries.map((e) => ({
          id: e.id,
          type: pascal(e.type) ?? e.type,
          pointsDelta: e.points,
          note: e.note,
          cashAmount: amount(e.cash),
          currency: e.currency,
          createdAt: e.createdAt,
          reason: pointsReason(e.type, e.details, e.externalId),
        })),
      })),
      null,
    ),
  allocatePoints: (cardholderId: string, points: number, note: string | null) =>
    write(
      api('POST', `/admin/cardholders/${seg(cardholderId)}/points/allocate`, {
        body: { points, note },
        idempotencyKey: newIdempotencyKey(),
      }),
    ),
  redeemPoints: async (cardholderId: string, points: number): Promise<RedeemResult> => {
    try {
      const r = await api<{
        status: string;
        cash: string;
        currency: string;
        balance: number;
      }>('POST', `/admin/cardholders/${seg(cardholderId)}/points/redeem`, {
        body: { points },
        idempotencyKey: newIdempotencyKey(),
      });
      const ok = r.status === 'succeeded';
      return {
        success: ok,
        message: ok
          ? 'Points redeemed.'
          : r.status === 'failed'
            ? 'The processor refused this; the points are back.'
            : 'The processor did not answer: the points are held until the load is checked under Loads.',
        newBalance: r.balance,
        cashAmount: Number(r.cash),
        currency: r.currency,
      };
    } catch (err) {
      if (!(err instanceof BffError)) throw err;
      return {
        success: false,
        message: err.message || 'Redemption failed.',
        newBalance: 0,
        cashAmount: 0,
        currency: '',
      };
    }
  },

  /** Loads waiting for a second admin, or with no answer from the processor (the Loads page). */
  loads: (f: { status?: string | null; channel?: string | null; page: number; pageSize: number }) =>
    orElse(
      api<ApiPage<LoadRow>>(
        'GET',
        `/admin/loads${query({ status: f.status, channel: f.channel, page: f.page, pageSize: f.pageSize })}`,
      ).then((p) => asPage(p, (l) => l)),
      emptyPage<LoadRow>(f.page, f.pageSize),
    ),
  approveLoad: (id: string) => write(api('POST', `/admin/loads/${seg(id)}/approve`)),
  rejectLoad: (id: string, note: string | null) =>
    write(api('POST', `/admin/loads/${seg(id)}/reject`, { body: { note } })),
  // ── Partner API clients of a program (docs/PARTNER_API.md § 2): secrets come back once, in the answer that makes them.
  partnerClients: (programId: string) =>
    orElse(api<PartnerClient[]>('GET', `/admin/programs/${seg(programId)}/partner-clients`), []),
  createPartnerClient: (programId: string, name: string) =>
    secretOrError(api<PartnerSecret>('POST', `/admin/programs/${seg(programId)}/partner-clients`, { body: { name } })),
  rotatePartnerClient: (id: string) =>
    secretOrError(api<PartnerSecret>('POST', `/admin/partner-clients/${seg(id)}/credentials`)),
  setPartnerClientStatus: (id: string, status: 'active' | 'disabled') =>
    write(api('PATCH', `/admin/partner-clients/${seg(id)}`, { body: { status } })),
  setPartnerWebhook: async (
    id: string,
    url: string | null,
  ): Promise<{ webhookSecret: string | null } | { error: string }> => {
    try {
      const r = await api<{ webhookSecret: string | null }>('PUT', `/admin/partner-clients/${seg(id)}/webhook`, {
        body: { url },
      });
      return { webhookSecret: r.webhookSecret };
    } catch (err) {
      if (err instanceof BffError) return { error: err.message };
      throw err;
    }
  },
  partnerDeliveries: (id: string) =>
    orElse(
      api<ApiPage<PartnerDelivery>>('GET', `/admin/partner-clients/${seg(id)}/deliveries?pageSize=10`).then(
        (p) => p.items,
      ),
      [],
    ),
  /** Undo a succeeded load at the processor (where it offers that), once. */
  reverseLoad: (id: string) =>
    write(api('POST', `/admin/loads/${seg(id)}/reverse`, { idempotencyKey: newIdempotencyKey() })),

  mdpStatus: () => orElse(api<MdpStatus>('GET', '/admin/processors/mdp/status'), null),
  probe: (provider: 'mdp' | 'zumrails' | 'intercash' | 'opencp') =>
    orElse(api<ProbeResult>('POST', `/admin/processors/${provider}/probe`), null),

  kiosk: (
    action: 'activate' | 'load' | 'status',
    body: { cardNumber?: string; cardReference?: string; amount?: number; fee?: number },
  ) => api<KioskExchange[]>('POST', `/admin/kiosk-simulator/${action}`, { body }),
  /** The processor QOR's staff look at: a choice kept in this browser (see shared/session). */
  setActiveProvider: (email: string, provider: string) => {
    storeProvider(email, provider);
    return Promise.resolve();
  },
};

export interface ProgramLimits {
  currency: string;
  maxLoad: string;
  dailyCardLoad: string;
  dailyProgramLoad: string | null;
  kycCardLimit: string | null;
  approvalThreshold: string | null;
  isDefault: boolean;
}

/** A load as the API lists it (snake_case status and channel, money as "25.00"). */
export interface PartnerClient {
  id: string;
  programId: string;
  name: string;
  clientId: string;
  status: 'active' | 'disabled';
  webhookUrl: string | null;
  webhookEvents: string[];
  hasWebhookSecret: boolean;
  previousSecretExpiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}
/** A client id with its secret: shown once. */
export interface PartnerSecret {
  client: PartnerClient;
  clientId: string;
  clientSecret: string;
}
export interface PartnerDelivery {
  id: string;
  eventType: string;
  status: 'pending' | 'delivered' | 'failed';
  attempts: number;
  lastStatus: number | null;
  lastError: string | null;
  nextAttemptAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

async function secretOrError(call: Promise<PartnerSecret>): Promise<PartnerSecret | { error: string }> {
  try {
    return await call;
  } catch (err) {
    if (err instanceof BffError) return { error: err.message };
    throw err;
  }
}

export interface LoadRow {
  id: string;
  cardholderId: string;
  cardId: string;
  programId: string;
  channel: string;
  /** credit = a load, debit = an unload */
  direction: 'credit' | 'debit';
  /** The kiosk's terminal id, for a kiosk load. */
  terminalId: string | null;
  amount: string;
  currency: string;
  status: string;
  message: string | null;
  note: string | null;
  reversedAt: string | null;
  /** A succeeded load the card's processor can still reverse. */
  reversible: boolean;
  /** id null: QOR itself asked. */
  requestedBy: { type: string; id: string | null };
  decidedBy: string | null;
  createdAt: string;
  completedAt: string | null;
}

// ───────────── settings, partner funds, approvals (docs/PARTNER_API.md § 4.5) ─────────────
// Not legacy pages: these keep the API's own shapes (money as "25.00", snake_case kinds).

export type SettingValue = boolean | number | string | string[] | null;
export interface SettingRow {
  key: string;
  group: string;
  groupLabel: string;
  label: string;
  help: string;
  type: 'bool' | 'money' | 'int' | 'percent' | 'ratio' | 'enum' | 'ipList' | 'textList';
  options: string[] | null;
  nullable: boolean;
  default: SettingValue;
  needsApproval: boolean;
  /** Changed by its own action (freeze / resume), not by the form. */
  managed: boolean;
  min: number | null;
  max: number | null;
  value: SettingValue;
  source: 'program' | 'parent' | 'platform' | 'default';
  own: SettingValue;
  hasOwn: boolean;
  pending: { approvalId: string; requestedBy: string; value: SettingValue; clear: boolean } | null;
}
export interface FundAccountView {
  id: string;
  provider: string;
  currency: string;
  available: string;
  held: string;
  advanceOutstanding: string;
  writtenOff: string;
  nextAdvanceDueAt: string | null;
  overdue: boolean;
}
export interface Approval {
  id: string;
  kind: string;
  programId: string | null;
  programName?: string | null;
  payload: {
    amount?: string;
    currency?: string;
    provider?: string;
    reference?: string;
    memo?: string;
    dueAt?: string;
    set?: Record<string, SettingValue>;
    clear?: string[];
    /** Staff confirmed this is another wire than a deposit of the same amount told lately. */
    confirmedNotDuplicate?: boolean;
  };
  status: 'pending' | 'approved' | 'rejected' | 'failed';
  requestedBy: { id: string; label: string };
  decidedBy: { id: string; label: string } | null;
  decidedAt: string | null;
  note: string | null;
  createdAt: string;
  /** A deposit's bills (the wire receipt). */
  bills?: DepositBill[];
  /** A deposit waiting: other deposits of the partner that may be the same wire (same reference or amount). */
  possibleDuplicates?: string[];
}
const BILL_EXTENSIONS: Record<string, string> = { 'application/pdf': 'pdf', 'image/png': 'png', 'image/jpeg': 'jpg' };
export interface DepositBill {
  id: string;
  contentType: string;
  size: number;
  sha256: string;
  createdAt: string;
}
export interface ProgramFunds {
  programId: string;
  name: string;
  provider: string | null;
  /** The program shown draws on its partner's fund (a location without its own). */
  isLocation: boolean;
  /** This fund is a location's own. */
  ownFund: boolean;
  /** The partner's locations that have their own fund. */
  locations: { programId: string; name: string; accounts: FundAccountView[] }[];
  enabled: boolean;
  frozen: boolean;
  advanceLimit: string | null;
  accounts: FundAccountView[];
  pending: Approval[];
}
export interface FundEntryView {
  id: string;
  kind: string;
  amount: string;
  advance: string;
  balanceAfter: string;
  loadId: string | null;
  approvalId: string | null;
  reference: string | null;
  memo: string | null;
  dueAt: string | null;
  createdBy: { type: string; id: string | null };
  createdAt: string;
}
export interface PartnerFundEntryView extends FundEntryView {
  programId: string | null;
  programName: string | null;
  currency: string | null;
}
export interface ProcessorSnapshotView {
  id: string;
  currency: string;
  balance: string;
  ledger: string;
  inFlight: string;
  difference: string;
  source: 'api' | 'manual';
  recordedBy: string | null;
  takenAt: string;
}
export type OpenCpAccount =
  | { configured: false }
  | { configured: true; cardId: string; available: string | null; ledger: string | null; currency: string };
export interface FundAlertView {
  key: string;
  level: 'warning' | 'critical';
  message: string;
  programId: string | null;
}
/** An alert as recorded: when it started, and who took it in hand. */
export interface FundAlertRecord extends FundAlertView {
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
}
export const AGING_BUCKETS = ['current', '1-7', '8-30', '31-90', '90+'] as const;
export type CollectionStatus = 'open' | 'promised' | 'disputed' | 'doubtful' | 'written_off' | 'closed';
export interface CollectionCaseView {
  id: string;
  status: CollectionStatus;
  assignee: { id: string; label: string | null } | null;
  promisedAt: string | null;
  openedAt: string;
  notes: { id: string; by: string; note: string; createdAt: string }[];
}
export interface Receivables {
  checkedAt: string;
  totals: {
    currency: string;
    outstanding: string;
    writtenOff: string;
    buckets: Record<(typeof AGING_BUCKETS)[number], string>;
  }[];
  items: {
    accountId: string;
    programId: string;
    name: string;
    currency: string;
    outstanding: string;
    writtenOff: string;
    nextDueAt: string | null;
    daysOverdue: number | null;
    bucket: (typeof AGING_BUCKETS)[number];
    collection: CollectionCaseView | null;
  }[];
}
export interface FundsHealth {
  checkedAt: string;
  frozen: boolean;
  processors: {
    provider: string;
    currency: string;
    hasLedger: boolean;
    ledger: string | null;
    partnerAvailable: string;
    partnerHeld: string;
    partnerTotal: string;
    inFlight: string;
    coverage: string | null;
    advancesOutstanding: string;
    snapshot: {
      balance: string;
      ledger: string;
      inFlight: string;
      difference: string;
      source: string;
      takenAt: string;
    } | null;
    snapshotStale: boolean;
    mismatch: boolean;
  }[];
  partners: {
    programId: string;
    name: string;
    enabled: boolean;
    frozen: boolean;
    provider: string | null;
    currency: string | null;
    available: string;
    held: string;
    advanceOutstanding: string;
    nextAdvanceDueAt: string | null;
    overdue: boolean;
    low: boolean;
  }[];
  unknownLoads: { count: number; late: number; oldestAt: string | null };
  alerts: FundAlertView[];
  risk: {
    markets: {
      provider: string;
      currency: string;
      balance: string;
      dailyLoad: string;
      runwayDays: string | null;
      unwired: string;
      unwiredSince: string | null;
      unwiredBusinessDays: number | null;
    }[];
    exposure: {
      currency: string;
      exposure: string;
      capital: string;
      usedPercent: string | null;
      stressLoss: string;
      debtors: { programId: string; name: string; owed: string }[];
    }[];
  };
}

/** A write that answers with data: the data, or the API's message (and, for settings, each field's problem). */
export type Outcome<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; problems: Record<string, string>; code: string; details: Record<string, unknown> };

async function outcome<T>(call: Promise<T>): Promise<Outcome<T>> {
  try {
    return { ok: true, data: await call };
  } catch (err) {
    if (!(err instanceof BffError)) throw err;
    const details = (err.body as { details?: Record<string, unknown> & { problems?: unknown } } | null)?.details;
    const problems =
      details?.problems && typeof details.problems === 'object' && !Array.isArray(details.problems)
        ? (details.problems as Record<string, string>)
        : {};
    return { ok: false, error: err.message, problems, code: err.code, details: details ?? {} };
  }
}

const scopePath = (programId: string | null) =>
  programId ? `/admin/programs/${seg(programId)}/settings` : '/admin/settings';

export const fundsApi = {
  settings: (programId: string | null) => orElse(api<SettingRow[]>('GET', scopePath(programId)), []),
  changeSettings: (programId: string | null, change: { set?: Record<string, SettingValue>; clear?: string[] }) =>
    outcome(
      api<{ approvalId: string | null; settings: SettingRow[] }>('PATCH', scopePath(programId), { body: change }),
    ),

  programFunds: (programId: string) =>
    orElse(api<ProgramFunds>('GET', `/admin/programs/${seg(programId)}/funds`), null),
  programEntries: (programId: string, page: number) =>
    orElse(
      api<ApiPage<FundEntryView>>(
        'GET',
        `/admin/programs/${seg(programId)}/funds/entries${query({ page, pageSize: 20 })}`,
      ),
      { items: [], total: 0, page, pageSize: 20 },
    ),
  requestFund: (
    programId: string,
    body: {
      kind: string;
      amount: string;
      currency: string;
      reference?: string;
      memo?: string;
      dueAt?: string;
      confirmNotDuplicate?: boolean;
    },
  ) => outcome(api<Approval>('POST', `/admin/programs/${seg(programId)}/funds/requests`, { body })),
  /** The file itself is the request body (PDF, PNG or JPEG, up to 5 MB). */
  addBill: (approvalId: string, file: File) =>
    outcome(api<DepositBill>('POST', `/admin/approvals/${seg(approvalId)}/bills`, { body: file })),
  /** The browser saves the bill (staff open it from there). */
  openBill: (approvalId: string, bill: DepositBill, index: number) =>
    download(
      `/admin/approvals/${seg(approvalId)}/bills/${seg(bill.id)}`,
      `deposit-${approvalId.slice(0, 8)}-bill-${index + 1}.${BILL_EXTENSIONS[bill.contentType] ?? 'bin'}`,
    ),
  createOwnFund: (programId: string) =>
    outcome(api<ProgramFunds>('POST', `/admin/programs/${seg(programId)}/funds/own`)),
  freeze: (programId: string | null) =>
    outcome(api('POST', programId ? `/admin/programs/${seg(programId)}/funds/freeze` : '/admin/funds/freeze')),
  resume: (programId: string | null) =>
    outcome(
      api<Approval>('POST', programId ? `/admin/programs/${seg(programId)}/funds/resume` : '/admin/funds/resume'),
    ),

  health: () => orElse(api<FundsHealth>('GET', '/admin/funds/health'), null),
  runChecks: () =>
    outcome(
      api<FundsHealth & { raised: number; repeated: number; resolved: number; frozen: string[] }>(
        'POST',
        '/admin/funds/checks',
      ),
    ),
  sendReport: () => outcome(api<{ sent: boolean }>('POST', '/admin/funds/report')),
  alertLog: () => orElse(api<FundAlertRecord[]>('GET', '/admin/funds/alerts'), []),
  acknowledgeAlert: (key: string) =>
    outcome(api<FundAlertRecord>('POST', '/admin/funds/alerts/acknowledge', { body: { key } })),
  receivables: () => orElse(api<Receivables>('GET', '/admin/funds/receivables'), null),
  updateCollection: (
    caseId: string,
    body: { status?: CollectionStatus; assigneeId?: string | null; promisedAt?: string | null },
  ) => outcome(api<CollectionCaseView>('PATCH', `/admin/funds/collections/${seg(caseId)}`, { body })),
  addCollectionNote: (caseId: string, note: string) =>
    outcome(api<CollectionCaseView>('POST', `/admin/funds/collections/${seg(caseId)}/notes`, { body: { note } })),
  /** QOR's own active staff: who may chase a debt. */
  qorStaff: async () =>
    (await orElse(api<ApiStaff[]>('GET', '/admin/staff'), []))
      .filter((s) => s.program.isInternal && s.status === 'active')
      .map((s) => ({ id: s.id, email: s.email })),
  /** QOR's ledger at a processor, newest first; `kinds` keeps only those movements (e.g. the wires). */
  processorEntries: (provider: string, currency: string, page: number, kinds?: readonly string[]) =>
    orElse(
      api<ApiPage<FundEntryView>>(
        'GET',
        `/admin/funds/processors/${seg(provider)}/entries${query({ currency, page, pageSize: 20, kind: kinds?.join(',') })}`,
      ),
      { items: [], total: 0, page, pageSize: 20 },
    ),
  /** Every reading of a processor's balance (from its API, or typed in), newest first. */
  processorSnapshots: (provider: string, currency: string, page: number) =>
    orElse(
      api<ApiPage<ProcessorSnapshotView>>(
        'GET',
        `/admin/funds/processors/${seg(provider)}/snapshots${query({ currency, page, pageSize: 20 })}`,
      ),
      { items: [], total: 0, page, pageSize: 20 },
    ),
  /** Central Payments' funding account (MFA), read live; system admins only. */
  openCpAccount: () => outcome(api<OpenCpAccount>('GET', '/admin/funds/processors/opencp/account')),
  /** Every partner fund's movements together, newest first. */
  partnerEntries: (filter: { programId?: string; kinds?: readonly string[] }, page: number) =>
    orElse(
      api<ApiPage<PartnerFundEntryView>>(
        'GET',
        `/admin/funds/partners/entries${query({ programId: filter.programId, kind: filter.kinds?.join(','), page, pageSize: 20 })}`,
      ),
      { items: [], total: 0, page, pageSize: 20 },
    ),
  requestProcessor: (body: {
    kind: 'topup' | 'adjustment';
    provider: string;
    amount: string;
    currency: string;
    reference?: string;
    memo?: string;
  }) => outcome(api<Approval>('POST', '/admin/funds/processors/requests', { body })),
  recordBalance: (body: { provider: string; currency: string; balance: string }) =>
    outcome(api('POST', '/admin/funds/snapshots', { body })),
  readInterCash: () => outcome(api<unknown[]>('POST', '/admin/funds/snapshots/intercash')),
  readCentralPayments: () => outcome(api<unknown[]>('POST', '/admin/funds/snapshots/opencp')),

  /** `scope`: one side of Funding only (a partner's fund, QOR's money at a processor, or the rest). */
  approvals: (status: string | null, page: number, scope?: 'partner' | 'processor' | 'other') =>
    orElse(
      api<ApiPage<Approval>>(
        'GET',
        `/admin/approvals${query({ status: status ?? undefined, scope, page, pageSize: 20 })}`,
      ),
      { items: [], total: 0, page, pageSize: 20 },
    ),
  approve: (id: string) => outcome(api<Approval>('POST', `/admin/approvals/${seg(id)}/approve`)),
  reject: (id: string, note: string | null) =>
    outcome(api<Approval>('POST', `/admin/approvals/${seg(id)}/reject`, { body: note ? { note } : {} })),

  /** A load the processor never answered, settled once checked with the processor. */
  resolveLoad: (id: string, outcome_: 'succeeded' | 'failed', note: string, reference: string | null) =>
    outcome(
      api('POST', `/admin/loads/${seg(id)}/resolve`, {
        body: { outcome: outcome_, note, ...(reference && { reference }) },
      }),
    ),
};

/** Why a points line exists, in words: the purchase and the rule (and why fewer points), or the reference. */
export function pointsReason(
  type: string,
  details: Record<string, unknown> | null,
  externalId: string | null,
): string | null {
  const d = details ?? {};
  const why = [
    d.cappedBy ? `capped by the ${String(d.cappedBy)}` : null,
    d.notCredited ? String(d.notCredited) : null,
    d.notTakenBack ? `${String(d.notTakenBack)} could not be taken back (not enough points left)` : null,
  ].filter(Boolean);
  if (type === 'earn' || type === 'earn_reversal') {
    const what = type === 'earn' ? 'Purchase' : 'Refund';
    const sum = `${String(d.amount)} ${String(d.currency)} × ${String(d.pointsPerUnit)} = ${String(d.computed)}`;
    return [`${what} at ${String(d.merchant ?? '—')} — ${String(d.rule)}: ${sum}`, ...why].join('; ');
  }
  return externalId ? `Reference ${externalId}` : null;
}

// ───────────── earning points, the points pool (docs/PARTNER_API.md § 4.6) ─────────────

export interface EarnRule {
  id: string;
  name: string;
  pointsPerUnit: string;
  minAmount: string | null;
  maxPointsPerTransaction: number | null;
  validFrom: string;
  validTo: string | null;
  isActive: boolean;
  inForce: boolean;
}
export interface PointsSummary {
  programId: string;
  name: string;
  earnEnabled: boolean;
  poolEnabled: boolean;
  pool: { balance: number };
  totals: { earned: number; allocated: number; redeemed: number; outstanding: number };
}
export interface PoolEntry {
  id: string;
  kind: string;
  points: number;
  balanceAfter: number;
  reference: string | null;
  memo: string | null;
  createdAt: string;
}
export type RuleInput = {
  name: string;
  pointsPerUnit: string;
  minAmount?: string | null;
  maxPointsPerTransaction?: number | null;
  validFrom?: string;
  validTo?: string | null;
};

export const pointsApi = {
  rules: (programId: string) => orElse(api<EarnRule[]>('GET', `/admin/programs/${seg(programId)}/points/rules`), []),
  addRule: (programId: string, body: RuleInput) =>
    outcome(api<EarnRule>('POST', `/admin/programs/${seg(programId)}/points/rules`, { body })),
  changeRule: (programId: string, ruleId: string, body: Partial<RuleInput> & { isActive?: boolean }) =>
    outcome(api<EarnRule>('PATCH', `/admin/programs/${seg(programId)}/points/rules/${seg(ruleId)}`, { body })),
  summary: (programId: string) =>
    orElse(api<PointsSummary>('GET', `/admin/programs/${seg(programId)}/points/summary`), null),
  poolEntries: (programId: string, page: number) =>
    orElse(
      api<ApiPage<PoolEntry>>(
        'GET',
        `/admin/programs/${seg(programId)}/points/pool/entries${query({ page, pageSize: 20 })}`,
      ),
      { items: [], total: 0, page, pageSize: 20 },
    ),
  requestPool: (
    programId: string,
    body: { kind: 'deposit' | 'adjustment'; points: number; reference?: string; memo?: string },
  ) => outcome(api<Approval>('POST', `/admin/programs/${seg(programId)}/points/pool/requests`, { body })),
  pendingFor: async (programId: string) =>
    (await fundsApi.approvals('pending', 1)).items.filter(
      (a) => a.programId === programId && a.kind.startsWith('points_pool_'),
    ),
};

// ── Kiosks (DESIGN § 8.1): the API's own shapes, money as "25.00" ──

export interface KioskDevice {
  id: string;
  code: string;
  name: string;
  /** The kiosk operator's own id of the machine (GSC's terminal id): every kiosk call must send it. */
  terminalId: string | null;
  programId: string;
  locationId: string;
  status: 'pending' | 'active' | 'disabled';
  maxLoad: string | null;
  dailyLoad: string | null;
  lastSeenAt: string | null;
  createdAt: string;
}
/** A client id and its secret: the secret is shown once, now, and never again. */
export interface KioskCredential {
  clientId: string;
  clientSecret: string;
}

export const kioskApi = {
  devices: () => orElse(api<KioskDevice[]>('GET', '/admin/devices'), []),
  create: (body: {
    code: string;
    name: string;
    programId: string;
    locationId?: string | null;
    terminalId?: string | null;
    maxLoad?: string | null;
  }) => outcome(api<{ device: KioskDevice } & KioskCredential>('POST', '/admin/devices', { body })),
  change: (
    id: string,
    body: {
      name?: string;
      terminalId?: string | null;
      maxLoad?: string | null;
      status?: 'active' | 'disabled';
      reason?: string | null;
    },
  ) => outcome(api<KioskDevice>('PATCH', `/admin/devices/${seg(id)}`, { body })),
  rotate: (id: string) => outcome(api<KioskCredential>('POST', `/admin/devices/${seg(id)}/credentials`)),

  // Card batches for kiosks (QOR staff; a system admin orders and cancels — docs/KIOSK_CARD_STOCK.md)
  batches: (programId: string) =>
    orElse(api<CardBatch[]>('GET', `/admin/card-batches${query({ programId })}`), [] as CardBatch[]),
  batch: (id: string) => outcome(api<CardBatch>('GET', `/admin/card-batches/${seg(id)}`)),
  orderBatch: (body: CardBatchOrder, idempotencyKey: string) =>
    outcome(api<CardBatch>('POST', '/admin/card-batches', { body, idempotencyKey })),
  cancelBatch: (id: string) => outcome(api<CardBatch>('DELETE', `/admin/card-batches/${seg(id)}`)),
  /** The card a card reference number names (sent in the body: a CRN never goes in a URL). */
  cardByReference: (cardReference: string) =>
    outcome(
      api<{ cardId: string; cardholderId: string; programId: string }>('POST', '/admin/cards/by-reference', {
        body: { cardReference },
      }),
    ),
  setWebAccess: (cardId: string, enabled: boolean) =>
    outcome(
      api<{ cardId: string; webAccess: boolean }>('PUT', `/admin/cards/${seg(cardId)}/web-access`, {
        body: { enabled },
      }),
    ),
};

/** A batch of anonymous cards ordered for a partner's kiosks; its cards are in stock until a kiosk sells them. */
export interface CardBatch {
  id: string;
  programId: string;
  deviceId: string | null;
  quantity: number;
  status: 'requested' | 'completed' | 'failed' | 'canceled';
  failureReason: string | null;
  shipTo: { line1: string; line2?: string | null; city: string; region: string; postalCode: string; country: string };
  cardsMade: number;
  inStock: number;
  sold: number;
  withoutReference: number;
  cardRefWeak: boolean;
  lastSyncedAt: string | null;
  createdAt: string;
}
export interface CardBatchOrder {
  programId: string;
  deviceId?: string | null;
  quantity: number;
  printedName?: string | null;
  shipTo: { line1: string; line2?: string | null; city: string; region: string; postalCode: string };
  contact: { name: string; phone: string };
}
